/**
 * Edge Fixer
 * Detects and fixes edges that cross through nodes.
 * Uses PathfindingRouter for obstacle-avoiding pathfinding.
 */

import type { ElkNode, ElkExtendedEdge } from 'elkjs';
import type { Point, Bounds, NodeWithBpmn } from '../../types/internal';
import { EVENT_TYPES, GATEWAY_TYPES, SUBPROCESS_TYPES, TASK_TYPES } from '../../types/bpmn-constants';
import { segmentCrossesNode, createOrthogonalPath } from './geometry-utils';
import { PathfindingRouter } from './pathfinding-router';
import { isDebugEnabled } from '../../utils/debug';

const SUBPROCESS_TYPE_SET = new Set<string>(SUBPROCESS_TYPES);
// Nodes that act as obstacles for edge routing. Detected by bpmn.type rather
// than ID prefix because IDs like `tx_end` (a transaction's internal end event)
// share a prefix with their parent and would slip past a regex-based filter.
const OBSTACLE_TYPE_SET = new Set<string>([
  ...EVENT_TYPES.filter((t) => t !== 'boundaryEvent'),
  ...TASK_TYPES,
  ...GATEWAY_TYPES,
  'callActivity',
  // Collapsed subprocesses are obstacles; expanded ones are containers and
  // handled by the isContainer branch below.
  ...SUBPROCESS_TYPES,
]);

// A node whose x/y is a coordinate system for its descendants and edges.
// Expanded subprocesses, pools, lanes, and processes all share this property:
// child positions and edge waypoints are stored relative to them.
function isContainer(bpmn: NodeWithBpmn['bpmn']): boolean {
  if (!bpmn) return false;
  const type = bpmn.type;
  if (!type) return false;
  if (type === 'participant' || type === 'lane' || type === 'process') return true;
  return bpmn.isExpanded === true && SUBPROCESS_TYPE_SET.has(type);
}

function isFlowObstacle(bpmn: NodeWithBpmn['bpmn']): boolean {
  if (!bpmn?.type) return false;
  if (bpmn.type === 'boundaryEvent') return false;
  // Skip expanded subprocesses — they're containers, not obstacles.
  if (bpmn.isExpanded === true && SUBPROCESS_TYPE_SET.has(bpmn.type)) return false;
  return OBSTACLE_TYPE_SET.has(bpmn.type);
}

/**
 * Handler for edge crossing detection and fixing
 */
export class EdgeFixer {
  private readonly margin = 15;
  private router: PathfindingRouter;

  constructor() {
    this.router = new PathfindingRouter({
      cellSize: 10,
      obstacleMargin: this.margin,
      allowDiagonal: false,
      gridPadding: 50,
    });
  }

  /**
   * Fix edges that cross through nodes
   */
  fix(graph: ElkNode): void {
    // Obstacles for crossing-detection. Excludes containers (expanded
    // subprocesses, pools/lanes) because edges crossing their borders is
    // expected behavior, not a routing failure.
    const obstaclesByContainer = new Map<string, Map<string, Bounds>>();
    // All anchorable nodes (obstacles + container nodes that can act as
    // edge source/target). Snap consults this so it can re-anchor edges
    // that start/end on a subprocess after post-processing resized it.
    const anchorsByContainer = new Map<string, Map<string, Bounds>>();
    const boundaryEventIds = new Set<string>();

    const isPoolType = (bpmn: NodeWithBpmn['bpmn']): boolean =>
      bpmn?.type === 'participant' || bpmn?.type === 'process';

    const recordNode = (
      bucket: Map<string, Map<string, Bounds>>,
      containerId: string,
      id: string,
      bounds: Bounds
    ) => {
      if (!bucket.has(containerId)) bucket.set(containerId, new Map());
      bucket.get(containerId)!.set(id, bounds);
    };

    const collectNodePositions = (
      node: ElkNode,
      offsetX: number = 0,
      offsetY: number = 0,
      containerId: string = 'root'
    ) => {
      const bpmn = (node as unknown as NodeWithBpmn).bpmn;
      const currentContainerId = isPoolType(bpmn) ? node.id : containerId;
      if (bpmn?.type === 'boundaryEvent') boundaryEventIds.add(node.id);

      if (node.x !== undefined && node.y !== undefined) {
        // Use visual dimensions for edge anchoring/crossing. ELK inflates
        // event heights (label band) and gateway widths (long-label band),
        // but edges should attach to the visible shape, not the layout box.
        const visualWidth = bpmn?._visualWidth ?? node.width ?? 100;
        const visualHeight = bpmn?._visualHeight ?? node.height ?? 80;
        const bounds: Bounds = {
          x: offsetX + node.x,
          y: offsetY + node.y,
          width: visualWidth,
          height: visualHeight,
        };
        const obstacle = isFlowObstacle(bpmn);
        // Subprocesses (and any container that can host edge endpoints) are
        // anchorable even though they aren't obstacles.
        const anchorable = obstacle || (bpmn?.isExpanded === true && SUBPROCESS_TYPE_SET.has(bpmn.type));
        if (obstacle) recordNode(obstaclesByContainer, currentContainerId, node.id, bounds);
        if (anchorable) recordNode(anchorsByContainer, currentContainerId, node.id, bounds);
      }

      if (node.children) {
        const descend = isContainer(bpmn);
        const newOffsetX = descend ? offsetX + (node.x ?? 0) : offsetX;
        const newOffsetY = descend ? offsetY + (node.y ?? 0) : offsetY;
        for (const child of node.children) {
          collectNodePositions(child, newOffsetX, newOffsetY, currentContainerId);
        }
      }
    };
    collectNodePositions(graph);

    const processEdges = (
      node: ElkNode,
      containerOffsetX: number = 0,
      containerOffsetY: number = 0,
      containerId: string = 'root'
    ) => {
      const bpmn = (node as unknown as NodeWithBpmn).bpmn;
      const currentContainerId = isPoolType(bpmn) ? node.id : containerId;

      // Edge waypoints on a container node are stored relative to that
      // container, so accumulate the container's own x/y before walking into
      // its edges (which are processed before recursing into children).
      const descend = isContainer(bpmn);
      const selfOffsetX = descend ? containerOffsetX + (node.x ?? 0) : containerOffsetX;
      const selfOffsetY = descend ? containerOffsetY + (node.y ?? 0) : containerOffsetY;

      if (node.edges) {
        const anchors = anchorsByContainer.get(currentContainerId) ?? new Map();
        const obstacles = obstaclesByContainer.get(currentContainerId) ?? new Map();

        for (const edge of node.edges) {
          if (!edge.sections || edge.sections.length === 0) continue;
          // Boundary-event edges have bespoke routing in diagram-builder;
          // re-snapping them here would discard that work.
          const sourceId = edge.sources?.[0];
          if (sourceId && boundaryEventIds.has(sourceId)) continue;
          this.snapEdgeEndpointsToNodeBorders(edge, anchors, selfOffsetX, selfOffsetY);
          this.fixEdgeIfCrossing(edge, anchors, obstacles, selfOffsetX, selfOffsetY);
        }
      }

      if (node.children) {
        for (const child of node.children) {
          processEdges(child, selfOffsetX, selfOffsetY, currentContainerId);
        }
      }
    };
    processEdges(graph);
  }

  private fixEdgeIfCrossing(
    edge: ElkExtendedEdge,
    anchors: Map<string, Bounds>,
    obstacleMap: Map<string, Bounds>,
    containerOffsetX: number,
    containerOffsetY: number
  ): void {
    const section = edge.sections?.[0];
    if (!section) return;

    const sourceId = edge.sources?.[0];
    const targetId = edge.targets?.[0];

    const waypoints: Point[] = [
      { x: containerOffsetX + section.startPoint.x, y: containerOffsetY + section.startPoint.y },
    ];
    if (section.bendPoints) {
      for (const bp of section.bendPoints) {
        waypoints.push({ x: containerOffsetX + bp.x, y: containerOffsetY + bp.y });
      }
    }
    waypoints.push({ x: containerOffsetX + section.endPoint.x, y: containerOffsetY + section.endPoint.y });

    const crossedNodes: string[] = [];
    for (let i = 0; i < waypoints.length - 1; i++) {
      const p1 = waypoints[i];
      const p2 = waypoints[i + 1];
      if (!p1 || !p2) continue;

      for (const [nodeId, pos] of obstacleMap) {
        if (nodeId === sourceId || nodeId === targetId) continue;
        if (segmentCrossesNode(p1, p2, pos)) {
          crossedNodes.push(nodeId);
        }
      }
    }

    const targetPos = targetId ? anchors.get(targetId) : undefined;
    const sourcePos = sourceId ? anchors.get(sourceId) : undefined;

    if (targetPos && sourcePos && waypoints.length >= 2) {
      const lastWaypoint = waypoints[waypoints.length - 1];
      const secondLastWaypoint = waypoints[waypoints.length - 2];
      const isReturnEdge = targetPos.y + targetPos.height < sourcePos.y;

      if (isReturnEdge && lastWaypoint && secondLastWaypoint) {
        if (Math.abs(secondLastWaypoint.y - lastWaypoint.y) < 5) {
          const segY = secondLastWaypoint.y;
          const segMinX = Math.min(secondLastWaypoint.x, lastWaypoint.x);
          const segMaxX = Math.max(secondLastWaypoint.x, lastWaypoint.x);

          if (segY > targetPos.y && segY < targetPos.y + targetPos.height) {
            if (segMinX < targetPos.x + targetPos.width && segMaxX > targetPos.x) {
              crossedNodes.push(targetId + ' (target)');
            }
          }
        }
      }
    }

    if (crossedNodes.length === 0) return;

    if (isDebugEnabled()) {
      console.log(`[BPMN] Edge ${edge.id} crosses nodes: ${crossedNodes.join(', ')}`);
    }

    if (!sourcePos || !targetPos) return;

    // Exclude source and target from obstacles. Otherwise the inflated
    // obstacle margin engulfs the start/end cells, the pathfinder can't
    // step out, and we fall back to a midpoint Z-shape that still cuts
    // straight through other nodes.
    const obstacles: Bounds[] = [];
    for (const [nodeId, pos] of obstacleMap) {
      if (nodeId === sourceId || nodeId === targetId) continue;
      obstacles.push(pos);
    }

    this.router.setObstacles(obstacles);

    const dx = (targetPos.x + targetPos.width / 2) - (sourcePos.x + sourcePos.width / 2);
    const dy = (targetPos.y + targetPos.height / 2) - (sourcePos.y + sourcePos.height / 2);

    // Pick the primary axis. When only one axis is cleanly separated, the
    // route HAS to leave/enter along it. When both are separated (i.e.,
    // source and target diagonal to each other with an obstacle band between),
    // pick the axis with the larger separation so the longer leg comes first
    // and the final segment is short and perpendicular to the target.
    const horizontallySeparated =
      targetPos.x >= sourcePos.x + sourcePos.width ||
      sourcePos.x >= targetPos.x + targetPos.width;
    const verticallySeparated =
      targetPos.y >= sourcePos.y + sourcePos.height ||
      sourcePos.y >= targetPos.y + targetPos.height;
    let horizontalPrimary: boolean;
    if (horizontallySeparated && !verticallySeparated) horizontalPrimary = true;
    else if (verticallySeparated && !horizontallySeparated) horizontalPrimary = false;
    else horizontalPrimary = Math.abs(dx) >= Math.abs(dy);

    let sourcePort: 'top' | 'bottom' | 'left' | 'right';
    let targetPort: 'top' | 'bottom' | 'left' | 'right';
    if (horizontalPrimary) {
      sourcePort = dx >= 0 ? 'right' : 'left';
      targetPort = dx >= 0 ? 'left' : 'right';
    } else {
      sourcePort = dy >= 0 ? 'bottom' : 'top';
      targetPort = dy >= 0 ? 'top' : 'bottom';
    }

    const result = this.router.routeEdge(sourcePos, targetPos, sourcePort, targetPort);
    // A* on a 10px grid emits a step-by-step path; collapse runs of stairs
    // into the longest L-shape that still clears every obstacle, otherwise
    // the diagram fills up with jagged staircases.
    const path = this.shortcutOrthogonal(result.path, obstacles);

    if (path.length < 2) return;

    // Re-anchor the target endpoint if the final segment's direction doesn't
    // match the requested side. When the route had to wrap around obstacles,
    // pathfinder's final L-leg can come in perpendicular to a DIFFERENT side
    // (e.g., vertical-into-left). Snapping to the matching side avoids
    // ensurePerpendicularEndpoints later inserting a tiny stub that bpmn.io
    // renders as an arrow tip pointing the wrong direction.
    this.alignFinalSegmentToTargetSide(path, targetPos);
    this.alignFinalSegmentToSourceSide(path, sourcePos);

    const startPoint = path[0];
    const endPoint = path[path.length - 1];
    const bendPoints = path.slice(1, -1);

    section.startPoint = {
      x: startPoint.x - containerOffsetX,
      y: startPoint.y - containerOffsetY,
    };
    section.endPoint = {
      x: endPoint.x - containerOffsetX,
      y: endPoint.y - containerOffsetY,
    };
    section.bendPoints = bendPoints.length > 0
      ? bendPoints.map(bp => ({
          x: bp.x - containerOffsetX,
          y: bp.y - containerOffsetY,
        }))
      : undefined;

    if (isDebugEnabled()) {
      console.log(`[BPMN] Fixed edge ${edge.id} with ${bendPoints.length} bend points using PathfindingRouter`);
    }
  }

  /**
   * Re-anchor edge start/end points to their source/target node borders.
   * Post-processing (compactor, lane stacking, etc.) often moves nodes after
   * ELK ran, leaving edge endpoints pointing at stale positions. Without this
   * pass, an edge can appear to continue past its target into empty space.
   *
   * The strategy is deliberately simple: snap endpoints to the natural border
   * for the source→target direction (horizontal-primary unless the nodes are
   * nearly column-aligned), and keep any bend points along the way intact.
   */
  private snapEdgeEndpointsToNodeBorders(
    edge: ElkExtendedEdge,
    nodePositions: Map<string, Bounds>,
    containerOffsetX: number,
    containerOffsetY: number
  ): void {
    const section = edge.sections?.[0];
    if (!section) return;

    const sourceId = edge.sources?.[0];
    const targetId = edge.targets?.[0];
    if (!sourceId || !targetId) return;

    const sourcePos = nodePositions.get(sourceId);
    const targetPos = nodePositions.get(targetId);
    if (!sourcePos || !targetPos) return;

    const srcCx = sourcePos.x + sourcePos.width / 2;
    const srcCy = sourcePos.y + sourcePos.height / 2;
    const dstCx = targetPos.x + targetPos.width / 2;
    const dstCy = targetPos.y + targetPos.height / 2;
    const dx = dstCx - srcCx;
    const dy = dstCy - srcCy;
    // If source and target are completely separated on one axis (one's far edge ≤
    // the other's near edge), the edge HAS to leave/enter via that axis — picking
    // the other axis sends the connector along the side of the source/target
    // bounding box and the perpendicular bend then often slices through whatever
    // node sits between them on the same layer. So whenever an axis is cleanly
    // separated, force that as the primary; only fall back to magnitude when both
    // axes overlap.
    const horizontallySeparated =
      targetPos.x >= sourcePos.x + sourcePos.width ||
      sourcePos.x >= targetPos.x + targetPos.width;
    const verticallySeparated =
      targetPos.y >= sourcePos.y + sourcePos.height ||
      sourcePos.y >= targetPos.y + targetPos.height;
    let horizontalPrimary: boolean;
    if (horizontallySeparated) horizontalPrimary = true;
    else if (verticallySeparated) horizontalPrimary = false;
    else horizontalPrimary = Math.abs(dx) >= Math.abs(dy);

    let startAbsX: number, startAbsY: number, endAbsX: number, endAbsY: number;
    if (horizontalPrimary) {
      startAbsY = srcCy;
      endAbsY = dstCy;
      if (dx >= 0) {
        startAbsX = sourcePos.x + sourcePos.width;
        endAbsX = targetPos.x;
      } else {
        startAbsX = sourcePos.x;
        endAbsX = targetPos.x + targetPos.width;
      }
    } else {
      startAbsX = srcCx;
      endAbsX = dstCx;
      if (dy >= 0) {
        startAbsY = sourcePos.y + sourcePos.height;
        endAbsY = targetPos.y;
      } else {
        startAbsY = sourcePos.y;
        endAbsY = targetPos.y + targetPos.height;
      }
    }

    // Emit a Z-shape so the downstream crossing check (which only inspects
    // axis-aligned segments) can see the actual route. A bare diagonal would
    // be invisible to the check, and diagram-builder would later orthogonalize
    // it straight through whatever node sits between source and target.
    const path = createOrthogonalPath(
      { x: startAbsX, y: startAbsY },
      { x: endAbsX, y: endAbsY },
      horizontalPrimary ? 'horizontal' : 'vertical'
    );
    const first = path[0];
    const last = path[path.length - 1];
    section.startPoint = {
      x: first.x - containerOffsetX,
      y: first.y - containerOffsetY,
    };
    section.endPoint = {
      x: last.x - containerOffsetX,
      y: last.y - containerOffsetY,
    };
    section.bendPoints = path.length > 2
      ? path.slice(1, -1).map((p) => ({
          x: p.x - containerOffsetX,
          y: p.y - containerOffsetY,
        }))
      : undefined;
  }

  // When the final segment is vertical but the endpoint sits on the target's
  // left/right edge (or horizontal but on top/bottom), swap the endpoint to
  // the side that matches the segment's direction. Keeps the route a clean L
  // instead of producing a stubby corner.
  private alignFinalSegmentToTargetSide(path: Point[], targetPos: Bounds): void {
    if (path.length < 2) return;
    const end = path[path.length - 1];
    const prev = path[path.length - 2];
    const segHorizontal = Math.abs(end.y - prev.y) < 1;
    const segVertical = Math.abs(end.x - prev.x) < 1;
    if (!segHorizontal && !segVertical) return;

    const left = targetPos.x;
    const right = targetPos.x + targetPos.width;
    const top = targetPos.y;
    const bottom = targetPos.y + targetPos.height;
    const cx = targetPos.x + targetPos.width / 2;
    const cy = targetPos.y + targetPos.height / 2;
    const onLeft = Math.abs(end.x - left) < 3;
    const onRight = Math.abs(end.x - right) < 3;
    const onTop = Math.abs(end.y - top) < 3;
    const onBottom = Math.abs(end.y - bottom) < 3;

    if (segVertical && (onLeft || onRight)) {
      const approachFromBelow = prev.y > end.y;
      end.x = cx;
      end.y = approachFromBelow ? bottom : top;
      prev.x = cx;
    } else if (segHorizontal && (onTop || onBottom)) {
      const approachFromRight = prev.x > end.x;
      end.y = cy;
      end.x = approachFromRight ? right : left;
      prev.y = cy;
    }
  }

  private alignFinalSegmentToSourceSide(path: Point[], sourcePos: Bounds): void {
    if (path.length < 2) return;
    const start = path[0];
    const next = path[1];
    const segHorizontal = Math.abs(start.y - next.y) < 1;
    const segVertical = Math.abs(start.x - next.x) < 1;
    if (!segHorizontal && !segVertical) return;

    const left = sourcePos.x;
    const right = sourcePos.x + sourcePos.width;
    const top = sourcePos.y;
    const bottom = sourcePos.y + sourcePos.height;
    const cx = sourcePos.x + sourcePos.width / 2;
    const cy = sourcePos.y + sourcePos.height / 2;
    const onLeft = Math.abs(start.x - left) < 3;
    const onRight = Math.abs(start.x - right) < 3;
    const onTop = Math.abs(start.y - top) < 3;
    const onBottom = Math.abs(start.y - bottom) < 3;

    if (segVertical && (onLeft || onRight)) {
      const departsUp = next.y < start.y;
      start.x = cx;
      start.y = departsUp ? top : bottom;
      next.x = cx;
    } else if (segHorizontal && (onTop || onBottom)) {
      const departsLeft = next.x < start.x;
      start.y = cy;
      start.x = departsLeft ? left : right;
      next.y = cy;
    }
  }

  // Greedy orthogonal "string pulling" — A* on a 10px grid emits step-by-step
  // staircases; for each anchor, jump to the farthest path point still reachable
  // via a single L-shape (or straight segment) that clears every obstacle.
  private shortcutOrthogonal(path: Point[], obstacles: Bounds[]): Point[] {
    if (path.length <= 2) return path;

    const segmentClear = (p1: Point, p2: Point): boolean => {
      for (const obs of obstacles) {
        if (segmentCrossesNode(p1, p2, obs)) return false;
      }
      return true;
    };

    // Try to connect a→b via a single L (or straight). Push the bend (if any)
    // and the endpoint to `out`; return false when no clearance is possible.
    const pushLShape = (a: Point, b: Point, out: Point[]): boolean => {
      if (Math.abs(a.x - b.x) < 1 || Math.abs(a.y - b.y) < 1) {
        if (!segmentClear(a, b)) return false;
        out.push(b);
        return true;
      }
      const hThenV = { x: b.x, y: a.y };
      if (segmentClear(a, hThenV) && segmentClear(hThenV, b)) {
        out.push(hThenV, b);
        return true;
      }
      const vThenH = { x: a.x, y: b.y };
      if (segmentClear(a, vThenH) && segmentClear(vThenH, b)) {
        out.push(vThenH, b);
        return true;
      }
      return false;
    };

    const result: Point[] = [path[0]];
    let i = 0;
    while (i < path.length - 1) {
      // Try the farthest jump first; A* only emits walkable steps so the
      // i→i+1 fallback is always reachable.
      for (let j = path.length - 1; j > i; j--) {
        if (j === i + 1 || pushLShape(path[i], path[j], result)) {
          if (j === i + 1) result.push(path[j]);
          i = j;
          break;
        }
      }
    }
    return result;
  }
}
